// AboutDignity (/about/dignity) — meet Dignity, the AHP healthcare advisor.
//
// Visual language matches /home-beta and the /hospital/:uuid card grid:
// patriotic palette (navy / red / gold / cream), Instrument Serif headlines,
// Instrument Sans body, eyebrow chips, alternating section backgrounds, and
// card-wrapped content with soft borders + subtle hover lifts. The narration
// player (DignityNarrationPlayer) sits between the hero and the long-form
// sections; lines map to sections via LINE_TO_SECTION so the page scrolls
// the active section into view and applies a soft gold ring to it.

import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { ensureModelViewer } from '@/lib/ensureModelViewer';
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  BookOpen, Stethoscope, Search, Megaphone, Quote, Heart,
  ArrowRight, FileText, Building2, Sparkles,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { DignityNarrationPlayer } from "@/components/dignity/DignityNarrationPlayer";
import { useBrand } from "@/config/brands";

const PAGE_KEY = "about-dignity";
const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";

// Narration line → page section. Driven by DignityNarrationPlayer's
// onLineChange callback; the page scrolls the matching section into view
// and lights it with a soft gold ring so the visitor can follow along.
const LINE_TO_SECTION: Record<number, string> = {
  0: 'section-origin', 1: 'section-origin',
  2: 'section-why',    3: 'section-why',
  4: 'section-what',   5: 'section-what',  6: 'section-what',
  7: 'section-movement', 8: 'section-movement', 9: 'section-movement',
  10: 'section-origin', 11: 'section-origin',
  12: 'section-dream',
  13: 'section-cta',
};

// ─── Theme tokens — same shape as HomeBeta / APIAccess / DataMethodology ──
function useThemeTokens() {
  const { isDark } = useTheme();
  return useMemo(() => {
    if (isDark) {
      return {
        bg: "#0a1628", bgAlt: "#050e1a", bgInverse: "#FAF9F6",
        text: "#FAF9F6", textOnInverse: "#1B1B1B",
        textMuted: "rgba(250,249,246,0.6)", textMutedOnInverse: "rgba(27,27,27,0.62)",
        gold: "#C5A55A", red: "#B22234", blue: "#5B9BD5", navy: "#002868",
        green: "#6bc9a0",
        cardBg: "#0d1e35", cardBorder: "rgba(250,249,246,0.10)",
        cardBgOnInverse: "rgba(0,40,104,0.04)", cardBorderOnInverse: "rgba(27,27,27,0.08)",
        divider: "rgba(250,249,246,0.10)", chipBg: "rgba(197,165,90,0.10)",
        activeRing: "rgba(197,165,90,0.45)",
      };
    }
    return {
      bg: "#FAF9F6", bgAlt: "#ffffff", bgInverse: "#0a1628",
      text: "#1B1B1B", textOnInverse: "#FAF9F6",
      textMuted: "rgba(27,27,27,0.62)", textMutedOnInverse: "rgba(250,249,246,0.65)",
      gold: "#C5A55A", red: "#B22234", blue: "#002868", navy: "#002868",
      green: "#2b5e49",
      cardBg: "#ffffff", cardBorder: "rgba(27,27,27,0.08)",
      cardBgOnInverse: "rgba(0,40,104,0.06)", cardBorderOnInverse: "rgba(250,249,246,0.12)",
      divider: "rgba(27,27,27,0.08)", chipBg: "rgba(197,165,90,0.10)",
      activeRing: "rgba(197,165,90,0.55)",
    };
  }, [isDark]);
}

type T = ReturnType<typeof useThemeTokens>;

// ─── Reusable building blocks (mirrors HomeBeta / APIAccess) ──────────────
function Eyebrow({ T, color }: { T: T; color?: string; children?: React.ReactNode } & { children: React.ReactNode }) {
  return null; // typed correctly below via casts at call sites
}

function SectionEyebrow({ T, color, children }: { T: T; color?: string; children: React.ReactNode }) {
  return (
    <span
      className="inline-block"
      style={{
        fontFamily: sans,
        fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase",
        color: color || T.gold, fontWeight: 700,
        background: T.chipBg,
        padding: "4px 10px", borderRadius: 4,
        marginBottom: 14,
      }}
    >
      {children}
    </span>
  );
}

function SectionHeading({ T, children }: { T: T; children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontFamily: serif,
        fontSize: "clamp(28px, 3.4vw, 38px)",
        lineHeight: 1.1,
        color: T.text,
        letterSpacing: "-0.01em",
        margin: 0,
      }}
    >
      {children}
    </h2>
  );
}

function SectionBodyText({ T, children }: { T: T; children: React.ReactNode }) {
  return (
    <p style={{ fontFamily: sans, fontSize: 16, lineHeight: 1.7, color: T.text, marginBottom: 16 }}>
      {children}
    </p>
  );
}

// Wraps each long-form section in a card frame, dims it slightly when
// inactive, lights it with a gold ring + bg when the narration is on that
// section. Wrap is `<section>` so scroll targets remain semantically correct.
function NarratedSection({
  T, id, eyebrow, eyebrowColor, title, active, children,
}: {
  T: T;
  id: string;
  eyebrow: string;
  eyebrowColor?: string;
  title: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      style={{
        background: active ? `${T.gold}0E` : T.cardBg,
        border: `1px solid ${active ? T.activeRing : T.cardBorder}`,
        borderRadius: 12,
        padding: "32px clamp(20px, 4vw, 40px)",
        transition: "background 0.4s ease, border-color 0.4s ease, transform 0.4s ease",
        transform: active ? "translateY(-2px)" : "translateY(0)",
        boxShadow: active ? "0 10px 30px rgba(0,40,104,0.06)" : "none",
        scrollMarginTop: 24,
      }}
    >
      <SectionEyebrow T={T} color={eyebrowColor}>{eyebrow}</SectionEyebrow>
      <SectionHeading T={T}>{title}</SectionHeading>
      <div style={{ marginTop: 20 }}>{children}</div>
    </section>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────
export default function AboutDignity() {
  const brand = useBrand();
  const T = useThemeTokens();
  const { isDark } = useTheme();
  const viewerRef = useRef<HTMLElement>(null);
  const [modelViewerReady, setModelViewerReady] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const handleLineChange = useCallback((lineIdx: number) => {
    const sectionId = LINE_TO_SECTION[lineIdx] || null;
    setActiveSection(sectionId);
    if (sectionId) {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const [webglSupported] = useState(() => {
    try {
      const c = document.createElement("canvas");
      return !!(c.getContext("webgl2") || c.getContext("webgl") || c.getContext("experimental-webgl"));
    } catch { return false; }
  });

  // Load model-viewer lazily — keep main bundle slim on devices that won't render it
  useEffect(() => {
    if (!webglSupported) return;
    if (!document.querySelector('link[href="/consumer/dignity_bill.glb"]')) {
      const link = document.createElement("link");
      link.rel = "preload";
      link.href = "/consumer/dignity_bill.glb";
      link.as = "fetch";
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    }
    if (customElements.get("model-viewer")) {
      setModelViewerReady(true);
      return;
    }
    const timer = setTimeout(() => {
      ensureModelViewer().then(() => {
        customElements.whenDefined("model-viewer").then(() => setModelViewerReady(true));
      }).catch(() => { /* WebGL init failed → PNG fallback path renders below */ });
    }, 200);
    return () => clearTimeout(timer);
  }, [webglSupported]);

  // ±30° head-turn oscillation — matches the homepage hero so Dignity feels alive
  useEffect(() => {
    if (!modelViewerReady) return;
    const AMPLITUDE = 30;
    const PERIOD = 5;
    const startTime = performance.now();
    const timer = setInterval(() => {
      const elapsed = (performance.now() - startTime) / 1000;
      const theta = AMPLITUDE * Math.sin((2 * Math.PI * elapsed) / PERIOD);
      const viewer = viewerRef.current as unknown as { cameraOrbit?: string };
      if (viewer?.cameraOrbit !== undefined) {
        viewer.cameraOrbit = `${theta}deg 75deg 105%`;
      }
    }, 100);
    return () => clearInterval(timer);
  }, [modelViewerReady]);

  // Loader stars (used while WebGL boots — decorative only)
  const stars = useMemo(() => {
    const s: { x: number; y: number; delay: number; size: number }[] = [];
    for (let i = 0; i < 13; i++) {
      s.push({
        x: 20 + Math.random() * 60,
        y: 10 + Math.random() * 80,
        delay: Math.random() * 1.5,
        size: 6 + Math.random() * 8,
      });
    }
    return s;
  }, []);

  // "What Dignity Does" cards — lucide icons + AHP card framing.
  const whatCards: Array<{ Icon: React.ElementType; title: string; body: string; color: string }> = [
    {
      Icon: BookOpen,
      title: "Educates",
      body: "Plain-English guides to thousands of medical procedures, billing codes, and hospital pricing — so you understand exactly what you're being charged for.",
      color: T.green,
    },
    {
      Icon: Stethoscope,
      title: "Advises",
      body: "An AI-powered advisor who answers your healthcare questions without judgment, jargon, or a sales pitch. Real answers from real data.",
      color: T.navy,
    },
    {
      Icon: Search,
      title: "Exposes",
      body: "Shines a light on extreme hospital markups, price gouging, and the billing practices that hurt American families every day.",
      color: T.red,
    },
    {
      Icon: Megaphone,
      title: "Advocates",
      body: "Stands for the grassroots movement demanding that healthcare pricing be transparent, fair, and accountable to the people it serves.",
      color: T.gold,
    },
  ];

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, fontFamily: sans }}>
      <Helmet>
        <title>Dignity — America's Healthcare Advisor | {brand.name}</title>
        <meta name="description" content="Meet Dignity — a hospital bill that got tired of destroying families and switched sides. Dignity is America's healthcare advisor: every price, every code, every trick the system uses, broken down in plain English." />
        <meta property="og:title" content="Meet Dignity — America's Healthcare Advisor" />
        <meta property="og:description" content="Meet Dignity — a hospital bill that got tired of destroying families and switched sides." />
        <meta property="og:image" content="https://americanhospitalprices.com/og-dignity.jpg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://americanhospitalprices.com/og-dignity.jpg" />
        <link rel="canonical" href="https://americanhospitalprices.com/about/dignity" />
      </Helmet>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section style={{ background: T.bgAlt, borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1100, paddingTop: 72, paddingBottom: 56 }}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
            {/* Left: 3D Dignity (or PNG / loader) — same model + animation as HomeBeta */}
            <div className="md:col-span-5 flex items-center justify-center">
              <div
                style={{
                  width: "clamp(280px, 36vw, 440px)",
                  height: "clamp(320px, 42vw, 520px)",
                  position: "relative",
                }}
              >
                {!webglSupported && (
                  <img
                    src="/consumer/dignity-transparent.webp"
                    alt="Dignity — America's Healthcare Advisor"
                    style={{
                      width: "100%", height: "100%", objectFit: "contain",
                      filter: "drop-shadow(0 12px 32px rgba(0,40,104,0.18))",
                      animation: "dignityFloat 4.2s ease-in-out infinite",
                    }}
                  />
                )}

                {webglSupported && !modelViewerReady && (
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 16, overflow: "hidden" }}>
                    {/* Flag-stripe loader; subdued so it doesn't shout */}
                    <div style={{ position: "absolute", inset: 0, opacity: 0.10 }}>
                      {Array.from({ length: 13 }).map((_, i) => (
                        <div key={i} style={{ height: `${100 / 13}%`, background: i % 2 === 0 ? T.red : "white", animation: `flagWave 3s ease-in-out ${i * 0.1}s infinite` }} />
                      ))}
                    </div>
                    <div style={{ position: "absolute", top: 0, left: 0, width: "40%", height: "54%", background: `${T.navy}1A`, borderRadius: "16px 0 0 0" }}>
                      {stars.map((s, i) => (
                        <div key={i} style={{ position: "absolute", left: `${s.x}%`, top: `${s.y}%`, width: s.size, height: s.size, color: T.gold, fontSize: s.size, lineHeight: 1, opacity: 0.6, animation: `starTwinkle 1.5s ease-in-out ${s.delay}s infinite` }}>★</div>
                      ))}
                    </div>
                    <div style={{ position: "relative", zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 56, height: 56, border: `3px solid ${T.cardBorder}`, borderTopColor: T.gold, borderRightColor: T.red, borderBottomColor: T.navy, borderRadius: "50%", animation: "dignitySpinner 1.2s linear infinite" }} />
                      <div style={{ fontSize: 11, fontWeight: 700, color: T.gold, letterSpacing: "2.5px", textTransform: "uppercase", animation: "dignityPulse 2s ease-in-out infinite" }}>Loading Dignity…</div>
                    </div>
                  </div>
                )}

                {webglSupported && modelViewerReady && (
                  <div style={{ display: "inline-block", animation: "dignityFloat 4.2s ease-in-out infinite" }}>
                    {/* @ts-expect-error - model-viewer web component */}
                    <model-viewer
                      ref={viewerRef as never}
                      src="/consumer/dignity_bill.glb"
                      alt="Dignity — America's Healthcare Advisor"
                      shadow-intensity="1"
                      exposure="1.1"
                      interaction-prompt="none"
                      camera-orbit="0deg 75deg 105%"
                      style={{ width: "clamp(280px, 36vw, 440px)", height: "clamp(320px, 42vw, 520px)", background: "transparent", display: "block" } as React.CSSProperties}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Right: headline + sub */}
            <div className="md:col-span-7">
              <span
                className="inline-flex items-center gap-1.5"
                style={{
                  fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase",
                  color: T.gold, fontWeight: 700,
                  background: T.chipBg, padding: "5px 12px", borderRadius: 4,
                  marginBottom: 18,
                }}
              >
                <Sparkles className="w-3.5 h-3.5" />
                America's healthcare advisor
              </span>
              <h1
                style={{
                  fontFamily: serif,
                  fontSize: "clamp(40px, 5.8vw, 72px)",
                  lineHeight: 1.02,
                  letterSpacing: "-0.02em",
                  margin: 0,
                  color: T.text,
                }}
              >
                Meet <em style={{ fontStyle: "italic", color: T.red }}>Dignity</em>.
              </h1>
              <p
                style={{
                  fontFamily: serif,
                  fontSize: "clamp(18px, 2.2vw, 24px)",
                  color: T.navy,
                  fontStyle: "italic",
                  lineHeight: 1.4,
                  marginTop: 18,
                  marginBottom: 28,
                  maxWidth: 560,
                }}
              >
                The accumulation of every excess charge across 9,000+ American hospitals —
                fighting for the belief that healthcare is a human right, not a profit center.
              </p>

              {/* Hero CTAs match HomeBeta primary/secondary buttons */}
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/scenarios"
                  className="inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5"
                  style={{
                    background: T.gold, color: "#1B1B1B",
                    padding: "12px 22px", borderRadius: 4,
                    fontWeight: 700, fontSize: "0.9rem", textDecoration: "none",
                  }}
                >
                  Explore Dignity's guides <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/hospital-rankings"
                  className="inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5"
                  style={{
                    background: "transparent", color: T.text,
                    border: `1px solid ${T.cardBorder}`,
                    padding: "11px 22px", borderRadius: 4,
                    fontWeight: 600, fontSize: "0.9rem", textDecoration: "none",
                  }}
                >
                  <Building2 className="w-4 h-4" />
                  Compare hospital prices
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── NARRATION + LONG-FORM ───────────────────────────────────────── */}
      <section style={{ background: T.bg }}>
        <div className="mx-auto px-6" style={{ maxWidth: 880, paddingTop: 48, paddingBottom: 80 }}>
          {/* Narration player drives scroll + active-section highlight below */}
          <DignityNarrationPlayer pageKey={PAGE_KEY} onLineChange={handleLineChange} />

          <div className="mt-10 space-y-6">
            {/* ── Origin — first-person opener ────────────────────────── */}
            <NarratedSection
              T={T}
              id="section-origin"
              eyebrow="In Dignity's own words"
              eyebrowColor={T.navy}
              title="I'm just a bill — and I decided to fight back."
              active={activeSection === "section-origin"}
            >
              <div
                style={{
                  borderLeft: `3px solid ${T.gold}`,
                  paddingLeft: 20,
                  fontStyle: "italic",
                  color: T.text,
                  lineHeight: 1.75,
                  fontSize: 16,
                  position: "relative",
                }}
              >
                <Quote
                  className="w-6 h-6"
                  style={{ position: "absolute", top: -4, left: -2, color: T.gold, opacity: 0.55 }}
                />
                <p style={{ marginTop: 8 }}>
                  I'm just a bill. But not just any bill — I'm the accumulation of every excess
                  charge, every inflated markup, every $47 aspirin and $12,000 MRI that American
                  hospitals have piled onto families who just needed help. I'm what happens when
                  you add up all the overcharging across 9,000+ hospitals. And I decided it was
                  time to fight back.
                </p>
              </div>
            </NarratedSection>

            {/* ── Why ─────────────────────────────────────────────────── */}
            <NarratedSection
              T={T}
              id="section-why"
              eyebrow="Why I exist"
              eyebrowColor={T.red}
              title="The same MRI costs $400 here and $12,000 down the street."
              active={activeSection === "section-why"}
            >
              <SectionBodyText T={T}>
                In every other developed nation on Earth, people don't go bankrupt because they
                got sick. They don't skip medications because they can't afford them. They don't
                avoid the emergency room because they're afraid of a bill that could cost them
                their home.
              </SectionBodyText>
              <SectionBodyText T={T}>
                In America, we have a system where the same MRI can cost $400 at one hospital
                and $12,000 at another — in the same city, on the same street. Where a hospital
                can charge $47 for a single aspirin. Where a routine blood test can generate a
                bill larger than a month's rent.
              </SectionBodyText>
              <p
                style={{
                  background: `${T.gold}14`,
                  border: `1px solid ${T.gold}33`,
                  borderRadius: 8,
                  padding: "14px 18px",
                  fontFamily: serif, fontStyle: "italic",
                  fontSize: "1.05rem", lineHeight: 1.55,
                  color: T.text,
                  margin: 0,
                }}
              >
                In 2026, with the technology and wealth humanity has achieved, no one should be
                financially destroyed by a medical event.
              </p>
            </NarratedSection>

            {/* ── What I do ───────────────────────────────────────────── */}
            <NarratedSection
              T={T}
              id="section-what"
              eyebrow="What I do"
              eyebrowColor={T.green}
              title="Educate. Advise. Expose. Advocate."
              active={activeSection === "section-what"}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                {whatCards.map(({ Icon, title, body, color }) => (
                  <div
                    key={title}
                    className="transition-transform hover:-translate-y-0.5"
                    style={{
                      background: T.bgAlt,
                      border: `1px solid ${T.cardBorder}`,
                      borderRadius: 10,
                      padding: 20,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {/* Subtle color accent bar — same trick as AtlasCard on /api */}
                    <div
                      style={{
                        position: "absolute", top: 0, left: 0,
                        width: 3, height: "100%", background: color,
                      }}
                    />
                    <div
                      className="inline-flex items-center justify-center"
                      style={{
                        width: 36, height: 36, borderRadius: 8,
                        background: `${color}18`, color, marginBottom: 12,
                      }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div
                      style={{
                        fontFamily: sans, fontSize: 11, fontWeight: 700,
                        color, textTransform: "uppercase", letterSpacing: "1.5px",
                        marginBottom: 6,
                      }}
                    >
                      {title}
                    </div>
                    <div style={{ fontFamily: sans, fontSize: 14, lineHeight: 1.6, color: T.text }}>
                      {body}
                    </div>
                  </div>
                ))}
              </div>
            </NarratedSection>

            {/* ── Movement ────────────────────────────────────────────── */}
            <NarratedSection
              T={T}
              id="section-movement"
              eyebrow="The movement"
              eyebrowColor={T.gold}
              title="Healthcare as a profit-maximization machine is a choice."
              active={activeSection === "section-movement"}
            >
              <SectionBodyText T={T}>
                Every other developed nation has figured this out. Universal coverage. Regulated
                pricing. A system where getting sick doesn't mean choosing between your health
                and your home.
              </SectionBodyText>
              <SectionBodyText T={T}>
                The United States spends more on healthcare than any country in the world — and
                gets worse outcomes. Not because our doctors aren't talented. Not because our
                technology isn't advanced. But because we've allowed healthcare to become a
                profit-maximization machine where human lives are line items on a balance sheet.
              </SectionBodyText>
              <p style={{ fontFamily: sans, fontSize: 15, fontWeight: 600, color: T.text, marginTop: 24, marginBottom: 12 }}>
                Dignity represents the growing movement of Americans who believe:
              </p>
              <ul className="space-y-2.5" style={{ paddingLeft: 0, listStyle: "none", margin: 0 }}>
                {[
                  "Healthcare pricing must be transparent — no more hidden charges and surprise bills.",
                  "No family should go bankrupt from a medical event.",
                  "Hospital pricing should be regulated to prevent the 100× markups we see today.",
                  "Every person deserves access to affordable care, regardless of income or employment.",
                  "Technology should empower patients, not obscure what they owe.",
                ].map((point) => (
                  <li
                    key={point}
                    className="flex items-start gap-3"
                    style={{ fontFamily: sans, fontSize: 15, lineHeight: 1.6, color: T.text }}
                  >
                    <span
                      style={{
                        color: T.gold,
                        fontSize: 12,
                        marginTop: 5,
                        flexShrink: 0,
                      }}
                      aria-hidden
                    >
                      ★
                    </span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <SectionBodyText T={T}>
                This isn't a political stance. It's a human one. People across the spectrum
                agree: the system is broken, and transparency is the first step to fixing it.
              </SectionBodyText>
            </NarratedSection>

            {/* ── Dream ───────────────────────────────────────────────── */}
            <NarratedSection
              T={T}
              id="section-dream"
              eyebrow="Dignity's dream"
              eyebrowColor={T.gold}
              title="One day, I want to become a real bill."
              active={activeSection === "section-dream"}
            >
              <div className="flex items-start gap-4">
                <div
                  className="flex-shrink-0 inline-flex items-center justify-center"
                  style={{
                    width: 48, height: 48, borderRadius: 12,
                    background: `${T.gold}1A`, color: T.gold,
                  }}
                >
                  <FileText className="w-6 h-6" />
                </div>
                <p
                  style={{
                    fontFamily: serif,
                    fontSize: "1.15rem",
                    lineHeight: 1.55,
                    color: T.text,
                    fontStyle: "italic",
                    margin: 0,
                  }}
                >
                  With your help, I hope to one day become a <strong>real</strong> bill —
                  legislation that brings dignity to all Americans by imposing strict price and
                  procedure caps across the entire medical industry. So that once and for all,
                  people's lives will no longer be something for someone to make a profit on.
                </p>
              </div>
            </NarratedSection>
          </div>

          {/* ── CTA strip ─────────────────────────────────────────────── */}
          <section
            id="section-cta"
            style={{
              marginTop: 56,
              background: T.bgAlt,
              border: `1px solid ${T.cardBorder}`,
              borderRadius: 12,
              padding: "40px clamp(20px, 4vw, 40px)",
              textAlign: "center",
            }}
          >
            <Heart className="w-6 h-6" style={{ color: T.red, margin: "0 auto 14px" }} />
            <h2
              style={{
                fontFamily: serif,
                fontSize: "clamp(24px, 3vw, 32px)",
                lineHeight: 1.15,
                color: T.text,
                margin: "0 0 12px",
                letterSpacing: "-0.01em",
              }}
            >
              Walk a bill with Dignity.
            </h2>
            <p
              style={{
                fontFamily: sans, fontSize: 15, lineHeight: 1.6,
                color: T.textMuted,
                maxWidth: 580, margin: "0 auto 24px",
              }}
            >
              Every medical event on the site is broken down line-by-line, with Dignity narrating
              what each charge means and how it compares across hospitals.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/scenarios"
                className="inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5"
                style={{
                  background: T.gold, color: "#1B1B1B",
                  padding: "12px 26px", borderRadius: 4,
                  fontWeight: 700, fontSize: "0.9rem", textDecoration: "none",
                }}
              >
                <BookOpen className="w-4 h-4" />
                Explore Dignity's guides
              </Link>
              <Link
                to="/hospital-rankings"
                className="inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5"
                style={{
                  background: T.navy, color: "#FAF9F6",
                  padding: "12px 26px", borderRadius: 4,
                  fontWeight: 700, fontSize: "0.9rem", textDecoration: "none",
                }}
              >
                <Building2 className="w-4 h-4" />
                Compare hospital prices
              </Link>
            </div>
            <p
              style={{
                fontFamily: sans, fontSize: 12,
                color: T.textMuted, marginTop: 22,
                letterSpacing: 0.2,
              }}
            >
              Built by the {brand.name} team and community.
            </p>
          </section>
        </div>
      </section>

      {/* ── Animations ──────────────────────────────────────────────────── */}
      <style>{`
        @keyframes dignityFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-10px); }
        }
        @keyframes dignitySpinner { to { transform: rotate(360deg); } }
        @keyframes dignityPulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.4; }
        }
        @keyframes starTwinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50%      { opacity: 1;   transform: scale(1.3); }
        }
        @keyframes flagWave {
          0%, 100% { transform: translateX(0); }
          25%      { transform: translateX(3px); }
          75%      { transform: translateX(-3px); }
        }
      `}</style>
    </div>
  );
}
